import React, { useState } from 'react';
import { Slider } from './ui/slider';
import { Calculator, ArrowRight, TrendingDown } from 'lucide-react';
import { Reveal } from './Reveal';
import { BOOKING_URL, CTA_LABEL } from '../data/constants';

const HIREKA_PRICE = 7999;
const fmt = (n) => Math.round(n).toLocaleString('de-DE') + ' €';

const SavingsCalculator = () => {
  const [salary, setSalary] = useState(65000);
  const [rate, setRate] = useState(25);

  const traditional = salary * (rate / 100);
  const low = salary * 0.2;
  const high = salary * 0.35;
  const savings = Math.max(traditional - HIREKA_PRICE, 0);
  const pct = traditional > 0 ? Math.max(Math.round((savings / traditional) * 100), 0) : 0;

  return (
    <Reveal delay={0.1} className="mt-20" data-testid="savings-calculator">
      <div className="lux-card p-8 lg:p-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-obsidian flex items-center justify-center"><Calculator className="w-5 h-5 text-amber-400" /></div>
            <div>
              <p className="eyebrow text-violet-700 !text-[11px]"><span className="eyebrow-line" />Festpreis-Rechner</p>
              <h3 className="text-xl md:text-2xl font-bold text-slate-900 mt-1">Was sparst du mit HIREKA?</h3>
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-end justify-between mb-3">
              <label className="text-sm font-medium text-slate-600">Jahresgehalt der Position</label>
              <span className="font-display text-2xl font-extrabold text-slate-900" data-testid="calc-salary-value">{fmt(salary)}</span>
            </div>
            <Slider value={[salary]} min={30000} max={200000} step={1000} onValueChange={(v) => setSalary(v[0])} className="[&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-amber-400 [&_[role=slider]]:bg-white" data-testid="calc-salary-slider" />
            <div className="flex justify-between text-xs text-slate-400 mt-2"><span>30.000 €</span><span>200.000 €</span></div>
          </div>

          <div className="mt-8">
            <div className="flex items-end justify-between mb-3">
              <label className="text-sm font-medium text-slate-600">Übliche Vermittler-Provision</label>
              <span className="font-display text-2xl font-extrabold text-slate-900" data-testid="calc-rate-value">{rate} %</span>
            </div>
            <Slider value={[rate]} min={20} max={35} step={1} onValueChange={(v) => setRate(v[0])} className="[&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-amber-400 [&_[role=slider]]:bg-white" data-testid="calc-rate-slider" />
            <div className="flex justify-between text-xs text-slate-400 mt-2"><span>20 %</span><span>35 %</span></div>
          </div>
        </div>

        <div className="lg:col-span-6 lux-card-dark p-7 lg:p-8 text-white relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-violet-600/30 blur-3xl" />
          <div className="relative grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Klassischer Vermittler</p>
              <p className="mt-2 font-display text-2xl md:text-3xl font-extrabold text-slate-400 line-through decoration-rose-400/70" data-testid="calc-traditional">{fmt(traditional)}</p>
              <p className="text-[11px] text-slate-500 mt-1">Spanne: {fmt(low)} – {fmt(high)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-amber-300 font-semibold">HIREKA Festpreis</p>
              <p className="mt-2 font-display text-2xl md:text-3xl font-extrabold text-gold" data-testid="calc-hireka">{fmt(HIREKA_PRICE)}</p>
              <p className="text-[11px] text-slate-500 mt-1">zzgl. MwSt. · jede Position</p>
            </div>
          </div>
          <div className="relative mt-7 pt-6 border-t border-white/10">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold flex items-center gap-2"><TrendingDown className="w-4 h-4 text-emerald-400" /> Deine Ersparnis</p>
            <div className="mt-2 flex items-end gap-3 flex-wrap">
              <span className="font-display text-4xl md:text-5xl font-extrabold text-white" data-testid="calc-savings">{fmt(savings)}</span>
              <span className="mb-1.5 inline-flex items-center rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-sm font-bold px-3 py-1" data-testid="calc-savings-pct">−{pct} %</span>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-400 to-amber-400 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="btn-gold relative mt-7 w-full px-6 py-3.5 text-sm text-center" data-testid="calc-cta">
            {CTA_LABEL} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </Reveal>
  );
};

export default SavingsCalculator;
